import { observable, makeObservable } from "mobx"
import { BaseStore } from "stores/base.store"
import { RootStore } from "stores/root.store"
import {
  applyAccountGameSpend,
  claimBonusSpendMissionPostback,
  claimDailyMissionPostback,
  getAccountBonusSpendStatus,
  getAccountDailyMissionProgress,
  tickAccountLoginStreak,
  tickDailyMissionForGame,
} from "../api/postbacks.call"
import { GamePlatformType } from "./account-games-BuffPlay.store"

// Per-game tickable daily-mission kinds (LOGIN_STREAK is account-wide; not in this set).
export const DailyMissionPostbackKind = {
  WATCH_AD: "WATCH_AD",
  DAILY_TASK: "DAILY_TASK",
} as const
export type DailyMissionPostbackKind = (typeof DailyMissionPostbackKind)[keyof typeof DailyMissionPostbackKind]

export const FullDailyMissionKind = {
  WATCH_AD: "WATCH_AD",
  DAILY_TASK: "DAILY_TASK",
  LOGIN_STREAK: "LOGIN_STREAK",
} as const
export type FullDailyMissionKind = (typeof FullDailyMissionKind)[keyof typeof FullDailyMissionKind]

// Mirrors buff-play-service AccountDailyMissionProgressOutputDto.
export interface AccountDailyMissionProgress {
  id: string
  kind: FullDailyMissionKind
  title: string
  requiredCount: number
  currentCount: number
  isClaimable: boolean
  isClaimed: boolean
  windowDays: number | null
  claimedAt: string | null
}

// Mirrors buff-play-service AccountBonusSpendTierStatusDto.
export interface BonusSpendTierStatus {
  id: string
  level: number
  spendThresholdUsd: number
  rewardUsd: number
  enabled: boolean
  isClaimable: boolean
  isClaimed: boolean
}

export interface AccountBonusSpendStatus {
  cumulativeAmount: number
  tiers: BonusSpendTierStatus[]
}

// Key under which per-(game, platform) progress is cached in the store so
// switching tabs in the admin modal doesn't show stale data from the other
// platform.
const cacheKey = (gameId: string, platform: GamePlatformType): string => `${gameId}::${platform}`

export class AccountPostbacksStore extends BaseStore {
  @observable
  public dailyMissionsByGameId: Record<string, AccountDailyMissionProgress[]> = {}

  @observable
  public bonusSpendByGameId: Record<string, AccountBonusSpendStatus | null> = {}

  @observable
  public isMutating: boolean = false

  constructor(rootStore: RootStore) {
    super(rootStore)
    makeObservable(this)
  }

  public async loadDailyMissions(accountId: string, gameId: string, platform: GamePlatformType) {
    const data = await this.defaultFunction(getAccountDailyMissionProgress, accountId, gameId, platform)
    if (Array.isArray(data)) {
      this.dailyMissionsByGameId = {
        ...this.dailyMissionsByGameId,
        [cacheKey(gameId, platform)]: data,
      }
    }

    return data
  }

  public getDailyMissions(gameId: string, platform: GamePlatformType): AccountDailyMissionProgress[] {
    return this.dailyMissionsByGameId[cacheKey(gameId, platform)] ?? []
  }

  public async tickDailyMission(
    accountId: string,
    gameId: string,
    kind: DailyMissionPostbackKind,
    platform: GamePlatformType,
  ) {
    this.isMutating = true
    try {
      const res = await this.defaultFunction(tickDailyMissionForGame, accountId, gameId, kind, platform)
      if (res?.error || res?.message) {
        throw new Error(Array.isArray(res?.message) ? res.message.join(", ") : res?.message || "Failed to tick mission")
      }

      return res
    } finally {
      this.isMutating = false
    }
  }

  public async claimDailyMission(accountId: string, missionId: string) {
    this.isMutating = true
    try {
      const res = await this.defaultFunction(claimDailyMissionPostback, accountId, missionId)
      if (res?.error || res?.message) {
        throw new Error(Array.isArray(res?.message) ? res.message.join(", ") : res?.message || "Failed to claim mission")
      }

      return res
    } finally {
      this.isMutating = false
    }
  }

  public async tickLoginStreak(accountId: string, platform: GamePlatformType = GamePlatformType.ANDROID) {
    this.isMutating = true
    try {
      const res = await this.defaultFunction(tickAccountLoginStreak, accountId, platform)
      if (res?.error || res?.message) {
        throw new Error(Array.isArray(res?.message) ? res.message.join(", ") : res?.message || "Failed to tick login streak")
      }

      return res
    } finally {
      this.isMutating = false
    }
  }

  public async loadBonusSpendStatus(accountId: string, gameId: string, platform: GamePlatformType) {
    const data = await this.defaultFunction(getAccountBonusSpendStatus, accountId, gameId, platform)
    if (data && typeof data === "object" && Array.isArray(data.tiers)) {
      this.bonusSpendByGameId = {
        ...this.bonusSpendByGameId,
        [cacheKey(gameId, platform)]: data,
      }
    }

    return data
  }

  public getBonusSpendStatus(gameId: string, platform: GamePlatformType): AccountBonusSpendStatus | null {
    return this.bonusSpendByGameId[cacheKey(gameId, platform)] ?? null
  }

  public async applySpend(accountId: string, gameId: string, amount: number, platform: GamePlatformType) {
    this.isMutating = true
    try {
      const res = await this.defaultFunction(applyAccountGameSpend, accountId, gameId, amount, platform)
      if (res?.error || res?.message) {
        throw new Error(Array.isArray(res?.message) ? res.message.join(", ") : res?.message || "Failed to apply spend")
      }

      return res
    } finally {
      this.isMutating = false
    }
  }

  public async claimBonusSpendMission(accountId: string, gameId: string, bonusSpendMissionId: string) {
    this.isMutating = true
    try {
      const res = await this.defaultFunction(claimBonusSpendMissionPostback, accountId, gameId, bonusSpendMissionId)
      if (res?.error || res?.message) {
        throw new Error(Array.isArray(res?.message) ? res.message.join(", ") : res?.message || "Failed to claim tier")
      }

      return res
    } finally {
      this.isMutating = false
    }
  }
}
